import { toast } from "react-toastify";
import { observable } from "mobx";
import { api, api_adm } from "../../api";
import { toast_error } from "../../utils";

export class _SugestoesMelhoriasAdminStore {

  obs = observable({
    loading: false as boolean,
    title_loading: "" as string,
    sugestoes_length: 0 as number,
    status_sugestao: [] as any,
    sugestoes: [] as any[]
  })

  /**
  * Consulta os status disponíveis para sugestões.
  *
  * Envia uma requisição GET para o endpoint responsável por listar os status.
  * Após receber uma resposta válida, atualiza `this.obs.status_sugestao` com
  * os registros retornados pela API.
  *
  * Em caso de erro na requisição, exibe uma mensagem informando que não foi
  * possível consultar os status.
  *
  * @async
  * @returns {Promise<boolean>} Retorna `true` quando a consulta é realizada com sucesso
  * e `false` quando ocorre algum erro ou a resposta não indica sucesso.
  */

  async handleConsultaStatus() {

    try {

      let resp = await api.get("/api/status");

      if (resp.data.ok) {
        this.obs.status_sugestao = resp.data.data;
        return true
      }
    } catch (ex) {
      toast_error(ex, `Erro ao consultar status, tente novamente mais tarde.`);
    }

    return false
  }

  /**
  * Consulta os detalhes de uma sugestão pelo identificador informado.
  *
  * Ativa o estado de carregamento da tela e envia uma requisição GET para o
  * endpoint administrativo responsável por buscar os detalhes da sugestão.
  * Quando a consulta é realizada com sucesso, retorna os dados recebidos da API.
  *
  * Em caso de erro na requisição, exibe uma mensagem informando que não foi
  * possível consultar os detalhes da sugestão. Ao final do processamento, o
  * estado de carregamento é desativado.
  *
  * @async
  * @param {string} id - Identificador da sugestão que será consultada.
  * @returns {Promise<any | null>} Retorna os dados da sugestão quando a consulta
  * é realizada com sucesso ou `null` em caso de erro ou resposta inválida.
  */

  async handleConsultaDetalhe(id: string) {

    try {

      this.obs.loading = true;
      this.obs.title_loading = "Buscando sugestão!";

      const resp = await api_adm.get(`/api/admin/sugestoes/detalhe/${id}`);

      if (resp.data.ok) {
        return resp.data.data;
      }

    } catch (ex) {

      toast_error(ex, "Erro ao consultar detalhes da sugestão.");

    } finally {

      this.obs.loading = false;

    }

    return null;
  }

  /**
  * Atualiza o status e a data prevista de uma sugestão.
  *
  * Ativa o estado de carregamento da tela e envia uma requisição PUT para o
  * endpoint administrativo responsável por editar a sugestão. Quando a operação
  * é realizada com sucesso, exibe uma mensagem de confirmação ao usuário.
  *
  * Em caso de erro na resposta ou na requisição, exibe uma mensagem informando
  * que não foi possível salvar as alterações. Ao final do processamento, o
  * estado de carregamento é desativado.
  *
  * @async
  * @param {string} id - Identificador da sugestão que será atualizada.
  * @param {number} status - Novo status da sugestão.
  * @param {string | null} data_prevista - Data prevista da sugestão ou `null` quando não informada.
  * @returns {Promise<boolean>} Retorna `true` quando a atualização é realizada com sucesso
  * e `false` quando ocorre algum erro ou a resposta não indica sucesso.
  */

  async handleAtualizarSugestao(id: string, status: number, data_prevista: string | null, motivo_recusa: string | null) {

    try {

      this.obs.loading = true;
      this.obs.title_loading = "Salvando alterações!";

      const resp = await api_adm.put(`/api/admin/sugestoes/edit/${id}`, {status: status, data_prevista: data_prevista, motivo_recusa: motivo_recusa});

      if (resp.data.ok) {

        toast.success("Alterações salvas com sucesso!");

        return true;
      }

      toast.error(resp.data.error || "Não foi possível salvar as alterações.");

    } catch (ex) {

      toast_error(ex, "Erro ao salvar as alterações da sugestão.");

    } finally {

      this.obs.loading = false;

    }

    return false;
  }

  /**
  * Consulta as sugestões de melhoria de forma paginada e filtrada por período.
  *
  * Ativa o estado de carregamento da tela e envia uma requisição POST para o
  * endpoint administrativo responsável por listar as sugestões. Após receber
  * a resposta, atualiza a lista de sugestões e a quantidade total de registros.
  *
  * Em caso de erro na requisição, exibe uma mensagem informando que não foi
  * possível consultar as sugestões. Ao final do processamento, o estado de
  * carregamento é desativado.
  *
  * @async
  * @param {number} page - Página atual da consulta.
  * @param {string} date1 - Data inicial do período utilizado como filtro.
  * @param {string} date2 - Data final do período utilizado como filtro.
  * @returns {Promise<boolean>} Retorna `true` quando a consulta é realizada com sucesso
  * e `false` quando ocorre algum erro ou a resposta não indica sucesso.
  */

  async handleConsultaSugestao(page: number = 1, date1: string = "", date2: string = "", status: number | null = null, data_entrega_inicial: string = "", data_entrega_final: string = "", id: string = "") {
    try {

      this.obs.loading = true;
      this.obs.title_loading = "Buscando sugestões!";

      const data = {
        page,
        limit: 15,
        date1,
        date2,
        status,
        data_entrega_inicial,
        data_entrega_final,
        id
      };

      const resp = await api_adm.post("/api/admin/sugestoes/index", data);

      if (resp.data.ok) {

        this.obs.sugestoes = resp.data.data || [];
        this.obs.sugestoes_length = resp.data.count || 0;

        return true;
      }

      return false;

    } catch (ex) {

      toast_error(ex, "Erro ao consultar sugestões, tente novamente mais tarde.");

      return false;

    } finally {

      this.obs.loading = false;

    }
  }

  /**
  * Realiza o download de um arquivo associado a uma sugestão.
  *
  * Envia uma requisição GET para o endpoint responsável pelo download do 
  * arquivo informado. A resposta é recebida no formato `blob`, permitindo 
  * o download de arquivos binários como imagens, PDFs e documentos.
  *
  * Quando o download é realizado com sucesso, cria temporariamente uma URL
  * local para o conteúdo recebido, gera um elemento de link e dispara o
  * download utilizando o nome original do arquivo. Após a operação, a URL
  * temporária e o elemento criado são removidos.
  *
  * Caso a API retorne uma resposta JSON em vez do arquivo esperado, exibe
  * uma mensagem informando que não foi possível realizar o download.
  *
  * Em caso de erro na requisição, exibe uma mensagem de erro. Ao final do
  * processamento, o estado de carregamento é desativado.
  *
  * @async
  * @param {string} sugestaoId - Identificador da sugestão à qual o arquivo pertence.
  * @param {number} indice - Índice do arquivo dentro da lista de arquivos da sugestão.
  * @param {string} nomeArquivo - Nome que será utilizado ao salvar o arquivo no dispositivo.
  * @returns {Promise<boolean>} Retorna `true` quando o download é iniciado com sucesso
  * e `false` quando ocorre algum erro ou a resposta da API não contém o arquivo esperado.
  */

  async handleDownloadArquivo(sugestaoId: string, indice: number, nomeArquivo: string) {

    try {

      this.obs.loading = true;
      this.obs.title_loading = "Baixando arquivo!";

      const resp = await api_adm.get(`/api/admin/sugestoes/${sugestaoId}/arquivos/${indice}/download`, {responseType: "blob"});

      const contentType = resp.headers["content-type"] || "";

      if (contentType.indexOf("application/json") !== -1) {

        toast.error("Não foi possível baixar o arquivo.");

        return false;

      }

      const url = window.URL.createObjectURL(resp.data);

      const link = document.createElement("a");

      link.href = url;
      link.download = nomeArquivo;

      document.body.appendChild(link);

      link.click();

      document.body.removeChild(link);

      window.URL.revokeObjectURL(url);

      return true;

    } catch (ex) {
      toast_error(
        ex,
        "Erro ao baixar arquivo, tente novamente mais tarde."
      );

      return false;

    } finally {
      this.obs.loading = false;
    }
  }

}

export const SugestoesMelhoriasAdminStore = new _SugestoesMelhoriasAdminStore();